import SearchInput from '../../../components/ui/SearchInput';
import { InfoState } from '../../../components/ui';
import { friendlyMessage } from '../../../components/ui/friendlyMessage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

import { toast } from '../../../components/ui/notify';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../../../services/user/notification.services';
import { hasNotificationOpenLink, resolveNotificationTarget } from '../../../utils/notificationDeepLink';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

function Notifications() {
  const { timeZone } = useTimezone();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [, setTotalCount] = useState(0);

  // Intersection Observer ref
  const observerRef = useRef();
  const lastNotificationRef = useRef();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchNotifications = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await getNotifications({
        page,
        limit: 20,
        search: debouncedSearchTerm
      });

      if (response && response.notifications) {
        const newNotifications = response.notifications || [];
        const pagination = response.pagination;

        if (append) {
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }

        setHasMore(pagination?.hasNextPage || false);
        setTotalCount(pagination?.totalCount || 0);
        setCurrentPage(page);
      } else {
        if (!append) {
          setNotifications([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      setError('Failed to load notifications');
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearchTerm]);

  // Fetch notifications on mount and when search changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchNotifications(1, false);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL || process.env.REACT_APP_API_URL;

    if (!token || !websocketUrl) {
      return undefined;
    }

    let userId = null;
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      userId = storedUser?.id || storedUser?.userId || null;
    } catch (error) {
      userId = null;
    }

    const socket = io(websocketUrl, {
      query: { token },
      transports: ['websocket', 'polling'],
    });

    const refreshNotifications = () => {
      setCurrentPage(1);
      setHasMore(true);
      fetchNotifications(1, false);
    };

    socket.on('connect', () => {
      if (userId) {
        socket.emit('joinRoom', userId);
      }
      socket.emit('getNotifications');
    });

    socket.on('pushNotifications', refreshNotifications);
    socket.on('connect_error', () => {
      // non-blocking: page still works via API fetch
    });

    return () => {
      socket.off('pushNotifications', refreshNotifications);
      socket.disconnect();
    };
  }, [fetchNotifications]);

  // Load more notifications
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchNotifications(currentPage + 1, true);
    }
  }, [loadingMore, hasMore, currentPage, fetchNotifications]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;

    if (lastNotificationRef.current) {
      observer.observe(lastNotificationRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, loadMore]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleNotificationClick = async (notification) => {
    try {
      const response = await markNotificationAsRead({ notificationId: notification.id });

      if (response.success) {
        const updatedNotifications = notifications.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        );
        setNotifications(updatedNotifications);
      }
    } catch (error) {
      // Marking read failed should not block opening the details page.
    }

    navigate('/user/notifications/notificationDetails', { state: { notification } });
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await markAllNotificationsAsRead();

      if (response.success) {
        const updatedNotifications = notifications.map(notification => ({
          ...notification,
          is_read: true
        }));
        setNotifications(updatedNotifications);
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleOpenNotificationLink = async (event, notification) => {
    event.stopPropagation();
    const target = resolveNotificationTarget(notification, localStorage.getItem('role'));
    if (!target) {
      return;
    }

    try {
      const response = await markNotificationAsRead({ notificationId: notification.id });
      if (response.success) {
        const updatedNotifications = notifications.map((n) =>
          n.id === notification.id ? { ...n, is_read: true } : n
        );
        setNotifications(updatedNotifications);
      }
    } catch (error) {
      // Non-blocking: still allow navigation to target page.
    }

    navigate(target);
  };

  if (loading && currentPage === 1) {
    return (
      <div className="tw-p-[20px] tw-m-[0_auto] max-[768px]:tw-p-[16px] notifications-container">
        <Loader variant="section" caption="Loading notifications..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-p-[20px] tw-m-[0_auto] max-[768px]:tw-p-[16px] notifications-container">
        <InfoState title="Notifications are taking a little longer" message={friendlyMessage(error)} onRetry={() => fetchNotifications(1, false)} />
      </div>
    );
  }

  return (
    <>
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-items-center tw-my-2"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full md:tw-w-1/3">
            <SearchInput
              type="text"
              className={"!tw-pl-[35px] tw-bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjExIiBjeT0iMTEiIHI9IjgiPjwvY2lyY2xlPjxsaW5lIHgxPSIyMSIgeTE9IjIxIiB4Mj0iMTYuNjUiIHkyPSIxNi42NSI+PC9saW5lPjwvc3ZnPg==)] tw-bg-no-repeat [transition:all_0.2s_ease] tw-p-[12px_16px] tw-border-solid tw-bg-[white] tw-text-[14px] [&:focus]:tw-shadow-[0_0_0_3px_rgba(74,_108,_247,_0.1)] [&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input search-input"}
              placeholder="Search notifications..."
              aria-label="Search notifications"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        <div className="tw-min-w-[0px] ui-column tw-w-full md:tw-w-2/3 tw-text-right tw-mt-2 md:tw-mt-0">
          <button
            className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-w-auto"}
            onClick={handleMarkAllAsRead}
            disabled={!notifications.some(n => !n.is_read)}
          >
            <span className="tw-hidden md:tw-block">
              <Icon icon="mdi:check-all" width="20" height="20" />&nbsp;
            </span>
            Mark all as read
          </button>
        </div>
      </div>

      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
        <div className="tw-ml-auto tw-mr-auto tw-w-full tw-max-w-[80rem] tw-pl-4 tw-pr-4 ui-container">
          <div className="tw-m-[0_auto] max-[768px]:tw-p-[16px] notifications-container tw-p-0 md:tw-p-2">
            <div className="tw-flex tw-flex-col tw-gap-[16px] notifications-list">
              {notifications.length > 0 ? (
                notifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    ref={index === notifications.length - 1 ? lastNotificationRef : null}
                    className={`tw-bg-[white] tw-border-[1px] tw-border-solid tw-border-[#eee] tw-rounded-[12px] tw-p-[20px] tw-mb-[16px] tw-cursor-pointer [transition:all_0.2s_ease] tw-shadow-[0_2px_4px_rgba(0,_0,_0,_0.05)] [&:hover]:tw-shadow-[0_4px_8px_rgba(0,_0,_0,_0.1)] [&:hover]:[transform:translateY(-2px)] [&:hover]:tw-bg-[#fafafa] [&:focus]:[outline:none] [&:focus]:tw-border-[#4A6CF7] [&:focus]:tw-shadow-[0_0_0_3px_rgba(74,_108,_247,_0.1)] [&.unread]:tw-border-l-[4px] [&.unread]:tw-border-solid [&.unread]:tw-border-l-[#4A6CF7] [&.unread]:tw-bg-[#f8f9ff] [&.unread:hover]:tw-bg-[#f0f2ff] max-[768px]:tw-p-[14px_16px] notification-item   ${!notification.is_read ? "unread" : ""}`}
                    onClick={() => handleNotificationClick(notification)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="tw-w-full notification-content">
                      <div className={"tw-flex tw-justify-between tw-items-center tw-mb-[8px] [&_h3]:tw-m-0 [&_h3]:tw-text-[16px] [&_h3]:tw-font-semibold [&_h3]:tw-text-[#333] max-[768px]:tw-text-[15px] notification-title"}>
                        <h3>{notification.title}</h3>
                        {!notification.is_read && (
                          <span className="tw-bg-[#4A6CF7] tw-text-[white] tw-p-[4px_8px] tw-rounded-[4px] tw-text-[12px] tw-font-medium unread-badge">New</span>
                        )}
                      </div>
                      <p className="tw-text-[#666] tw-text-[14px] tw-leading-[1.5] tw-m-[0_0_12px_0] notification-body">{notification.body}</p>
                      <div className="tw-flex tw-justify-between tw-items-center tw-text-[#888] tw-text-[12px] tw-gap-[16px] notification-meta">
                        <span className="tw-flex tw-items-center tw-gap-[6px] tw-text-[#888] tw-text-[13px] notification-date">
                          <Icon icon="mdi:clock-outline" width="16" height="16" />
                          <span>{formatUtcInTimeZone(notification.createdAt, timeZone, 'MMM DD, YYYY, hh:mm A')}</span>
                        </span>
                        {hasNotificationOpenLink(notification, localStorage.getItem('role')) && (
                          <button
                            type="button"
                            className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] tw-no-underline tw-m-0 tw-text-[rgb(64_84_214_/_var(--tw-text-opacity,_1))] [transition:opacity_0.2s_ease] tw-bg-[transparent] [--tw-text-opacity:1] [&:hover]:tw-opacity-[0.8] [&:hover]:tw-text-[#dc3545] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] ui-button ui-button-link tw-p-0 tw-ms-4"}
                            onClick={(e) => handleOpenNotificationLink(e, notification)}
                          >
                            Open link
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message={searchTerm ? "No notifications found matching your search." : "No notifications found."} />
              )}

              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="tw-text-center tw-py-4">
                  <Loader variant="inline" caption="Loading more notifications..." />
                </div>
              )}

              {/* End of List Indicator */}
              {!hasMore && notifications.length > 0 && (
                <div className="tw-text-center tw-py-4">
                  <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">
                    <Icon icon="mdi:check-circle" className="tw-me-2" />
                    You've seen all notifications
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Notifications;
